import {
  ALLOWED_CASE_TYPE_LABEL_KEY,
  ORDERED_CASE_TYPES,
} from '@app/constants/case-types';
import { getLocaleOption } from '@app/constants/locale-options';
import i18n, { NAMESPACE } from '@app/i18n';
import { guardCsvRow } from '@app/utils/csv-formula-guard';
import {
  CSV_CATALOG,
  localizedHeaders,
  UTF8_BOM,
} from '@app/utils/csv-headers';

// Dynamic import keeps papaparse out of the main entrypoint bundle
const loadPapa = () => import('papaparse').then((m) => m.default);

// Lazily loaded papaparse; module-private — callers should use usersJsonToCSV (async).
const jsonToCSV = async (json, delimiter, columns) => {
  const Papa = await loadPapa();
  return Papa.unparse(json, { delimiter, columns });
};

export const usersJsonToCSV = async (
  users,
  isTitleEnabled,
  isLanguageEnabled,
  exposureTypes = null,
) => {
  await i18n.loadNamespaces(NAMESPACE.TEAM_MANAGEMENT);
  const t = (key) =>
    i18n.t(`bulkUpload.export.${key}`, { ns: NAMESPACE.TEAM_MANAGEMENT });

  // Display name, not the code; every one parses back on re-upload.
  const languageLabel = (code) => {
    const option = getLocaleOption(code);
    return option ? i18n.t(option.labelKey, { ns: NAMESPACE.COMMON }) : '';
  };

  const caseTypesLabel = (caseTypes = []) =>
    ORDERED_CASE_TYPES.filter((caseType) => caseTypes?.includes(caseType))
      .map((caseType) =>
        i18n.t(ALLOWED_CASE_TYPE_LABEL_KEY[caseType], {
          ns: NAMESPACE.TEAM_MANAGEMENT,
        }),
      )
      .join('; ');

  const exposureTypesLabel = (exposureSkills = []) =>
    exposureTypes
      .filter(({ id }) => exposureSkills?.includes(id))
      .map(({ name }) => name)
      .join('; ');

  const keys = CSV_CATALOG.BULK_USERS_EXPORT.filter(
    (key) =>
      (isTitleEnabled || key !== 'title') &&
      (isLanguageEnabled || key !== 'language') &&
      (exposureTypes || key !== 'exposureTypes'),
  );
  const labels = localizedHeaders(keys);
  const labelFor = Object.fromEntries(keys.map((key, i) => [key, labels[i]]));

  const usersFormatted = users.map((user) =>
    guardCsvRow({
      [labelFor.uniqueId]: user.oktaUserId,
      [labelFor.firstName]: user?.profile?.firstName,
      [labelFor.lastName]: user?.profile?.lastName,
      ...(isTitleEnabled ? { [labelFor.title]: user?.profile?.title } : {}),
      [labelFor.lastLogin]: user?.lastLoginTime,
      [labelFor.phoneNumber]: user?.profile?.phoneNumber,
      [labelFor.officeLine]: user?.profile?.contactNumber,
      [labelFor.email]: user?.email,
      [labelFor.roles]: user?.roles?.join(', '),
      [labelFor.allowedCaseTypes]: caseTypesLabel(user?.allowedCaseTypes),
      ...(exposureTypes
        ? { [labelFor.exposureTypes]: exposureTypesLabel(user?.exposureSkills) }
        : {}),
      [labelFor.groupMembers]: user?.groups?.join('; '),
      [labelFor.groupLeads]: user?.groupLeads?.join('; '),
      ...(isLanguageEnabled
        ? { [labelFor.language]: languageLabel(user?.language) }
        : {}),
      [labelFor.active]: user?.active ? t('yes') : t('no'),
    }),
  );

  const csv = await jsonToCSV(usersFormatted, ',', labels);
  return `${UTF8_BOM}${csv}`;
};
